/* React hooks */
import {useEffect, useRef, useState} from "react";
import {getAffiliationList, updateDeceasedCorrespondent,} from "../../api/pro/deceasedReq";
import useAuth from "../../hooks/useAuth";
import {useLocation, useParams} from "react-router-dom";
import useDeceased from "../../hooks/useDeceased";
import CivilityForm from "../../components/common/input-layout/CivilityForm";
import LastNameForm from "../../components/common/input-layout/LastNameForm";
import FirstnameForm from "../../components/common/input-layout/FirstnameForm";
import InputFullAddress from "../../components/common/input-layout/InputFullAddress";
import {checkRequiredFields, getErrorListFromValidation, removeErrorList, validateFormInput, capitalize, upperCaseName, ucfirst, patternListChoices} from "../../helper/tools";
import useData from "../../hooks/useData";


export default function FormCorrespondentChoice({
    legend,
    deceasedInfos,
    apiDeceasedInfos,
    apiCorrespondentInfos,
    setShowModal,
    registerSubmitFunction,
    liveIsConjointAdmin,
    liveMaritalStatus,
    liveIsAdminFilled,
    onCorrespondentFilledChange,
    noSubmitButton = false,
}) {
    const isConjointAdmin = liveIsConjointAdmin !== undefined ? liveIsConjointAdmin : deceasedInfos?.isConjointAdmin;
    // En dehors du wizard (modales d'édition), le gestionnaire est déjà enregistré avant même
    // d'ouvrir ce formulaire : liveIsAdminFilled n'est alors pas fourni, on considère donc le
    // gestionnaire comme renseigné par défaut.
    const isAdminFilled = liveIsAdminFilled !== undefined ? liveIsAdminFilled : true;
    // Dans le wizard, le statut marital peut être modifié à l'étape 1 sans avoir encore été
    // enregistré/rechargé (deceasedInfos reste donc l'ancienne valeur) : se baser en priorité sur
    // liveMaritalStatus, remonté en direct par FormFamilyStatusInfos.
    const effectiveMaritalStatus = liveMaritalStatus !== undefined ? liveMaritalStatus : deceasedInfos?.maritalStatus;
    const { auth } = useAuth();
    const token = auth?.token;
    const { id } = useParams();
    const { pathname } = useLocation();
    const [values, setValues] = useState({});
    const [isFormModified, setIsFormModified] = useState(false);
    const fieldsetRef = useRef();
    const {childrenInfos, correspondentInfos, partnerInfos} = useDeceased();
    // Marié(e)/Pacsé(e)/Union libre : la question du conjoint doit être posée. Veuf(ve)/Divorcé(e)/
    // Célibataire/Séparé(e) : pas de question du conjoint, même si un conjoint existe encore dans
    // les infos du défunt (ex: renseigné avant le veuvage/divorce/séparation).
    // Si le gestionnaire EST déjà le conjoint (isConjointAdmin == 1), ne pas reposer la question :
    // "Est-ce le gestionnaire ?" suffit alors à couvrir le cas du conjoint, une question séparée
    // serait redondante.
    const showPartnerQuestion = !!partnerInfos?.id
        && isConjointAdmin != 1
        && effectiveMaritalStatus != 2 && effectiveMaritalStatus != 3
        && effectiveMaritalStatus != 4 && effectiveMaritalStatus != 7;
    const [ selectedChildId, setSelectedChildId ] = useState();
    const [ selectedChild, setSelectedChild ] = useState();
    const { affiliationList, setAffiliationList } = useDeceased();
    const [ adultChildrenList, setAdultChildrenList ] = useState();
    const [ defaultChildId, setDefaultChildId ] = useState();
    const affiliationRef = useRef();
    const { selectedCity } = useData();
    const [ newCorrespondent, setNewCorrespondent ] = useState();
    const isInitialized = useRef(false);
    const handleSubmitRef = useRef();

    useEffect(() => {
        if (selectedCity?.zip && selectedCity?.name) {
            setNewCorrespondent(prev => ({
                ...prev,
                address: {
                    ...prev?.address,
                    zipcode: selectedCity?.zip,
                    city: selectedCity?.name
                }
            }))

            setSelectedChild(prev => {
                return {
                    ...prev,
                    address: {
                        ...prev?.address,
                        zipcode: selectedCity?.zip,
                        city: selectedCity?.name
                    }
                };
            })
        }
    }, [selectedCity]);

    const onChangeSelectedChild = (e) => {
        setIsFormModified(true);
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setSelectedChild(prev => ({...prev, [e.target.name] : fieldValue }))
        validateFormInput(e)
    };

    const onChangeChildCorrespondent = (e) => {
        setIsFormModified(true);
        const newChildId = e.target.value;
        setSelectedChildId(newChildId);
        // Préremplissage immédiat des champs (civilité/nom/prénom/...) depuis childrenInfos, sans
        // attendre un futur cycle de rendu : l'ancien mécanisme (ref dans un tableau de dépendances
        // de useEffect, qui n'est pas réactif aux mutations de ref) ne se déclenchait jamais.
        const childData = childrenInfos?.find(child => newChildId == child?.id);
        if (childData) {
            setSelectedChild(childData);
        }
        validateFormInput(e)
    };

    const onChangeAddress = (e) => {
        setIsFormModified(true);
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        setSelectedChild(prev => ({
            ...prev,
            address: {
                ...(prev?.address || {}),
                [e.target.name]: fieldValue
            }
        }));
        validateFormInput(e);
    }

    const onChangeRadio = (e) => {
        setIsFormModified(true);
        let value = e.target.value;

        if (e.target.type === "radio" && (e.target.name === "isCorrespondentAdmin" || e.target.name === "isCorrespondentPartner" || e.target.name === "isCorrespondentChild")) {
            value = parseInt(value, 10);
        }

        setValues(prevValues => ({...prevValues, [e.target.name] : value }))
        validateFormInput(e);
    };

    const onChangeChildAddress = (e) => {
        setIsFormModified(true);
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        setSelectedChild(prev => ({
            ...prev,
            address: {
                ...(prev?.address || {}),
                [e.target.name]: fieldValue
            }
        }));

        validateFormInput(e)
    }

    const onChangeNewCorrespondent = (e) => {
        setIsFormModified(true);
        let fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        if (e.target.name === "familyAffiliation") {
            fieldValue = parseInt(fieldValue, 10);
        }
        setNewCorrespondent(prev => ({...prev, [e.target.name] : fieldValue }))
        validateFormInput(e)
    }

    const onChangeNewCorrespondentAddress = (e) => {
        setIsFormModified(true);
        const fieldValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        setNewCorrespondent(prev => ({
            ...prev,
            address: {
                ...(prev?.address || {}),
                [e.target.name]: fieldValue
            }
        }));
        validateFormInput(e);
    }

    const apiAffiliationList = async () => {
        const response = await getAffiliationList(token)
        setAffiliationList(response)
    }

    useEffect(() => {
        setDefaultChildId(childrenInfos?.find(child => child?.isCorrespondent)?.id)
    }, [ childrenInfos ]);

    useEffect(() => {
        setAdultChildrenList(childrenInfos?.filter(child => child?.isMinor == 0 && child?.isAdministrator == 0 && child?.isDeceased == 0))
    }, [ childrenInfos ]);

    useEffect(() => {
        // N'initialiser les valeurs QU'UNE SEULE FOIS au montage, une fois childrenInfos chargé
        // (nécessaire pour décider du défaut "enfants" ci-dessous).
        // Les appels API du wizard partent en parallèle : attendre aussi partnerInfos, sinon
        // showPartnerQuestion peut encore valoir false à cet instant et verrouiller un mauvais défaut.
        if (!isInitialized.current && (correspondentInfos || deceasedInfos) && childrenInfos !== null && partnerInfos !== undefined) {
            const sourceIsCorrespondentAdmin = correspondentInfos?.isCorrespondentAdmin !== undefined
                ? correspondentInfos.isCorrespondentAdmin
                : deceasedInfos?.isCorrespondentAdmin;

            const sourceIsCorrespondentPartner = correspondentInfos?.isCorrespondentPartner !== undefined
                ? correspondentInfos.isCorrespondentPartner
                : deceasedInfos?.isCorrespondentPartner;

            const sourceIsCorrespondentChild = correspondentInfos?.isCorrespondentChild !== undefined
                ? correspondentInfos.isCorrespondentChild
                : deceasedInfos?.isCorrespondentChild;

            // Pas de valeur existante : "Oui" par défaut (cette question est toujours affichée,
            // contrairement aux questions conjoint/enfant qui dépendent de showPartnerQuestion).
            const parsedIsCorrespondentAdmin = sourceIsCorrespondentAdmin !== null && sourceIsCorrespondentAdmin !== undefined
                ? (typeof sourceIsCorrespondentAdmin === 'boolean'
                    ? (sourceIsCorrespondentAdmin ? 1 : 0)
                    : parseInt(sourceIsCorrespondentAdmin, 10))
                : 1;

            const hasAdultChildren = childrenInfos?.some(child => child?.isMinor == 0 && child?.isAdministrator == 0 && child?.isDeceased == 0);

            // Pas de valeur existante : ne RIEN précocher ("Oui" par défaut masquait silencieusement
            // le choix réel de l'utilisateur — en sélectionnant "Non" à la question gestionnaire,
            // cette question "Est-elle celle du conjoint ?" apparaissait déjà cochée "Oui" sans
            // action de l'utilisateur ; s'il remplissait ensuite un correspondant manuel sans
            // remarquer/changer ce choix caché, l'enregistrement renvoyait quand même le conjoint à
            // la place du correspondant saisi). Laisser null force une réponse explicite (le radio
            // "Oui" reste "required", donc la validation du formulaire l'exigera).
            // Si la question n'est plus posée, ignorer une éventuelle ancienne valeur enregistrée :
            // sinon isCorrespondentPartner reste bloqué à 1 et masque à tort enfant/autre.
            const parsedIsCorrespondentPartner = !showPartnerQuestion
                ? 0
                : (sourceIsCorrespondentPartner !== null && sourceIsCorrespondentPartner !== undefined
                    ? (typeof sourceIsCorrespondentPartner === 'boolean'
                        ? (sourceIsCorrespondentPartner ? 1 : 0)
                        : parseInt(sourceIsCorrespondentPartner, 10))
                    : null);

            // Même raisonnement que pour isCorrespondentPartner ci-dessus : ne plus précocher "Oui"
            // par défaut (ça bloquait silencieusement l'affichage du formulaire "autre
            // correspondant" si l'utilisateur ne remarquait/changeait pas ce choix cocher sans
            // action, cf. condition d'affichage plus bas qui exige isCorrespondentChild != 1).
            const parsedIsCorrespondentChild = sourceIsCorrespondentChild !== null && sourceIsCorrespondentChild !== undefined
                ? (typeof sourceIsCorrespondentChild === 'boolean'
                    ? (sourceIsCorrespondentChild ? 1 : 0)
                    : parseInt(sourceIsCorrespondentChild, 10))
                : null;

            setValues({
                isCorrespondentAdmin: parsedIsCorrespondentAdmin,
                isCorrespondentPartner: parsedIsCorrespondentPartner,
                isCorrespondentChild: parsedIsCorrespondentChild
            });

            isInitialized.current = true;
        }
    }, [ correspondentInfos, deceasedInfos, childrenInfos, partnerInfos ]);

    // Si le statut marital change EN DIRECT après l'initialisation (ex: passage à veuf/divorcé/
    // séparé dans l'étape 1 du wizard alors que isCorrespondentPartner valait déjà 1), la question
    // du conjoint disparaît mais values.isCorrespondentPartner resterait sinon bloqué à 1,
    // masquant à tort les propositions "enfant"/"autre" qui en dépendent.
    useEffect(() => {
        if (isInitialized.current && !showPartnerQuestion) {
            setValues(prev => prev?.isCorrespondentPartner ? { ...prev, isCorrespondentPartner: 0 } : prev);
        }
    }, [ showPartnerQuestion ]);

    // Remonte au wizard si un correspondant est effectivement renseigné (gestionnaire, conjoint,
    // enfant sélectionné, ou "autre" avec civilité/nom/prénom saisis) : la question "le signataire
    // est-il le gestionnaire ou le correspondant ?" dans FormSignatory n'a de sens que si les deux
    // options proposées correspondent à quelqu'un de réellement choisi.
    useEffect(() => {
        if (!onCorrespondentFilledChange) {
            return;
        }
        const isCorrespondentFilled = values?.isCorrespondentAdmin === 1
            ? isAdminFilled
            : values?.isCorrespondentPartner === 1
                ? true
                : values?.isCorrespondentChild === 1
                    ? !!selectedChildId
                    : (values?.isCorrespondentAdmin === 0 && (values?.isCorrespondentPartner === 0 || values?.isCorrespondentPartner === null) && (values?.isCorrespondentChild === 0 || values?.isCorrespondentChild === null))
                        ? !!(newCorrespondent?.civility && newCorrespondent?.lastName && newCorrespondent?.firstName)
                        : false;
        onCorrespondentFilledChange(isCorrespondentFilled);
    }, [ values?.isCorrespondentAdmin, values?.isCorrespondentPartner, values?.isCorrespondentChild, selectedChildId, newCorrespondent?.civility, newCorrespondent?.lastName, newCorrespondent?.firstName, isAdminFilled ]);

    // Initialiser ou réinitialiser newCorrespondent quand correspondentInfos change UNIQUEMENT si newCorrespondent n'existe pas encore
    useEffect(() => {
        if (correspondentInfos && !newCorrespondent) {
            setNewCorrespondent({
                ...correspondentInfos,
                address: correspondentInfos?.address || {}
            });
        }
    }, [ correspondentInfos ]);

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Utiliser fieldsetRef pour la validation
        const formElement = fieldsetRef.current || e.target;
        if (!checkRequiredFields(formElement)) {
            return;
        }
        let body;
        if (values?.isCorrespondentAdmin == 1) {
            body = {
                deceasedId: id,
                isCorrespondentAdmin: 1,
                isCorrespondentChild: 0,
                isCorrespondentPartner: 0,
                familyAffiliation: 0,
            }
        } else if (values?.isCorrespondentPartner == 1) {
            body = {
                deceasedId: id,
                isCorrespondentAdmin: 0,
                isCorrespondentPartner: 1,
                isCorrespondentChild: 0,
                correspondentId: partnerInfos?.id,
                title: partnerInfos?.title,
                civility: partnerInfos?.civility,
                lastName: partnerInfos?.lastName,
                firstName: partnerInfos?.firstName,
                birthName: partnerInfos?.birthName,
                address: {
                    addressId: partnerInfos?.address?.addressId || partnerInfos?.actualplace?.addressId || 0,
                    address: partnerInfos?.address?.address || partnerInfos?.actualplace?.address,
                    addressComp: partnerInfos?.address?.addressComp || partnerInfos?.actualplace?.addressComp,
                    zipcode: partnerInfos?.address?.zipcode || partnerInfos?.actualplace?.zipcode,
                    city: partnerInfos?.address?.city || partnerInfos?.actualplace?.city,
                    country: partnerInfos?.address?.country || partnerInfos?.actualplace?.country || "FR"
                },
                phone: partnerInfos?.phone,
                mobile: partnerInfos?.mobile,
                email: partnerInfos?.email,
                familyAffiliation: 1,
            }
        } else if (values?.isCorrespondentChild == 1) {
            body = {
                deceasedId: id,
                isCorrespondentAdmin: 0,
                isCorrespondentPartner: 0,
                isCorrespondentChild: 1,
                childCorrespondent: selectedChildId,
                title: selectedChild?.title,
                civility: selectedChild?.civility,
                lastName: selectedChild?.lastName,
                firstName: selectedChild?.firstName,
                birthName: selectedChild?.birthName,
                address : {
                    addressId: selectedChild?.address?.addressId || 0,
                    address: selectedChild?.address?.address,
                    addressComp: selectedChild?.address?.addressComp,
                    zipcode: selectedChild?.address?.zipcode,
                    city: selectedChild?.address?.city,
                    country: selectedChild?.address?.country || "FR",
                },
                phone: selectedChild?.phone,
                mobile: selectedChild?.mobile,
                email: selectedChild?.email,
                familyAffiliation: 5,
            }
        } else {
            body = {
                deceasedId: id,
                isCorrespondentAdmin: 0,
                isCorrespondentPartner: 0,
                isCorrespondentChild: 0,
                correspondentId: newCorrespondent?.id || correspondentInfos?.id,
                title: newCorrespondent?.title,
                civility: newCorrespondent?.civility,
                lastName: newCorrespondent?.lastName,
                firstName: newCorrespondent?.firstName,
                birthName: newCorrespondent?.birthName,
                address: {
                    addressId: newCorrespondent?.address?.addressId || 0,
                    address: newCorrespondent?.address?.address,
                    addressComp: newCorrespondent?.address?.addressComp,
                    zipcode: newCorrespondent?.address?.zipcode,
                    city: newCorrespondent?.address?.city,
                    country: newCorrespondent?.address?.country || "FR"
                },
                phone: newCorrespondent?.phone,
                mobile: newCorrespondent?.mobile,
                email: newCorrespondent?.email,
                familyAffiliation: newCorrespondent?.familyAffiliation
            }
        }
        await updateDeceasedCorrespondent(token, body)
            .then(response => {
                removeErrorList();
                if (!response?.response?.data?.error) {
                    setIsFormModified(false);

                    // Afficher le message de succès dans le bon fieldset
                    const errorDiv = fieldsetRef.current?.querySelector('div.errors');
                    if (errorDiv) {
                        errorDiv.insertAdjacentHTML('beforeend', '<p class="success-message">Les modifications ont été enregistrées avec succès !</p>');

                        // Supprimer le message après 3 secondes
                        setTimeout(() => {
                            const successMsg = errorDiv.querySelector('.success-message');
                            if (successMsg) {
                                successMsg.remove();
                            }
                        }, 3000);
                    }

                    apiCorrespondentInfos();
                    apiDeceasedInfos();

                    if (!noSubmitButton && setShowModal) {
                        setShowModal(false);
                    }
                } else {
                    getErrorListFromValidation(response?.response?.data?.error)
                }
            })
    }


    useEffect(() => {
        apiAffiliationList()
    }, [ token ]);


    useEffect(() => {
        if (selectedChildId && childrenInfos) {
            const childData = childrenInfos?.find(child => selectedChildId == child?.id);
            if (childData) {
                setSelectedChild(childData);
            }
        }
    }, [ selectedChildId, childrenInfos ]);

    // Enregistrer la fonction de soumission auprès du formulaire parent.
    // handleSubmitRef est mis à jour de façon synchrone à CHAQUE rendu (pas via un effet, qui
    // accuserait un cycle de retard) : le wrapper enregistré une seule fois ci-dessous appelle
    // toujours la version la plus à jour de handleSubmit. Sans ça, un enregistrement différé
    // (dépendant de values/selectedChild) pouvait être encore en retard d'un cycle de rendu au
    // moment où le wizard déclenche la soumission (ex: juste après avoir choisi un enfant), et
    // soumettait alors un selectedChild périmé/vide.
    handleSubmitRef.current = handleSubmit;
    useEffect(() => {
        if (registerSubmitFunction) {
            registerSubmitFunction((e) => handleSubmitRef.current(e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const FormWrapper = noSubmitButton ? 'div' : 'form';
    const formProps = noSubmitButton ? {} : { onSubmit: handleSubmit, method: 'POST', noValidate: true };

    return (
        <FormWrapper {...formProps}>
            <fieldset ref={fieldsetRef}>
                <legend>{legend}</legend>
                { !isAdminFilled &&
                    <p className="info-message">Veuillez d'abord renseigner le gestionnaire.</p>
                }
                { isAdminFilled && <>
                <div className="form-group">
                    <div className="form-input">
                        <label htmlFor="isCorrespondentAdmin" className="required">L'adresse de correspondance des courriers après décès est-elle celle du gestionnaire ?</label>
                        <input type="radio" name="isCorrespondentAdmin" id="isCorrespondentAdmin1" value={1} required onChange={onChangeRadio}
                               checked={values?.isCorrespondentAdmin == 1}/><label htmlFor="isCorrespondentAdmin1">Oui</label>
                        <input type="radio" name="isCorrespondentAdmin" id="isCorrespondentAdmin0" value={0} onChange={onChangeRadio}
                               checked={values?.isCorrespondentAdmin == 0}/><label htmlFor="isCorrespondentAdmin0">Non</label>
                    </div>
                </div>
                    { (showPartnerQuestion && values?.isCorrespondentAdmin == 0) &&
                <div className="form-group">
                        <div className="form-input">
                            <label htmlFor="isCorrespondentPartner" className="required">Est-elle celle du conjoint ?</label>
                            <input type="radio" name="isCorrespondentPartner" id="isCorrespondentPartner1" value={1} required onChange={onChangeRadio}
                                   checked={values?.isCorrespondentPartner == 1}/><label htmlFor="isCorrespondentPartner1">Oui</label>
                            <input type="radio" name="isCorrespondentPartner" id="isCorrespondentPartner0" value={0} onChange={onChangeRadio}
                                   checked={values?.isCorrespondentPartner == 0}/><label htmlFor="isCorrespondentPartner0">Non</label>
                        </div>
                </div>
                    }



                { (adultChildrenList?.length > 0 && values?.isCorrespondentAdmin == 0  && (values?.isCorrespondentPartner == 0 || values?.isCorrespondentPartner == null)) &&
                    <>
                        <div className="form-group">
                            <div className="form-input">
                                <label htmlFor="isCorrespondentChild" className="required">Est-elle celle d'un de ses enfants ?</label>
                                <input type="radio" name="isCorrespondentChild" id="isCorrespondentChild1" value={1} required onChange={onChangeRadio}
                                       checked={values?.isCorrespondentChild == 1}/><label htmlFor="isCorrespondentChild1">Oui</label>
                                <input type="radio" name="isCorrespondentChild" id="isCorrespondentChild0" value={0} onChange={onChangeRadio}
                                       checked={values?.isCorrespondentChild == 0}/><label htmlFor="isCorrespondentChild0">Non</label>
                            </div>
                        </div>

                        { (values?.isCorrespondentChild == 1) &&
                            <>
                                <div className="form-input">
                                    <label htmlFor="childCorrespondent" className="required">Correspondant enfant</label>
                                    <select name="childCorrespondent" defaultValue={defaultChildId} required onChange={onChangeChildCorrespondent}>
                                        <option disabled="disabled">Sélectionnez un enfant</option>
                                        {adultChildrenList?.map((child, index) => (
                                            <option key={index} name="childCorrespondent" value={child?.id}>{capitalize(child?.firstName)} {upperCaseName(child?.lastName)}</option>
                                        ))}
                                    </select>
                                </div>
                                    <div className="form-group">
                                        <CivilityForm required={true} onChange={onChangeSelectedChild} value={selectedChild?.civility || ""}/>
                                    </div>

                                    {/* Champs saisis directement en contrôlé (plutôt que via LastNameForm/
                                        FirstnameForm, qui sont non-contrôlés en interne via defaultValue) :
                                        un remontage via key provoquait un dédoublement des champs affichés
                                        lors d'un changement rapide d'enfant sélectionné. */}
                                    <div className="form-input">
                                        <label htmlFor="lastName" className="required">Nom</label>
                                        <input
                                            pattern={patternListChoices?.lastname}
                                            type="text"
                                            name="lastName"
                                            value={selectedChild?.lastName || ""}
                                            onChange={(e) => { e.target.value = e.target.value.trim().toUpperCase(); onChangeSelectedChild(e); }}
                                            required
                                        />
                                    </div>
                                    <div className="form-input">
                                        <label htmlFor="birthName">Nom de naissance</label>
                                        <input type="text" name="birthName"
                                               value={selectedChild?.birthName || ""} onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChangeSelectedChild(e); }}/>
                                    </div>
                                    <div className="form-input">
                                        <label htmlFor="firstName" className="required">Prénom</label>
                                        <input
                                            pattern={patternListChoices?.firstname}
                                            type="text"
                                            name="firstName"
                                            value={selectedChild?.firstName || ""}
                                            onChange={(e) => { e.target.value = ucfirst(e.target.value.trim()); onChangeSelectedChild(e); }}
                                            required
                                        />
                                    </div>
                                    <InputFullAddress
                                        addressField="address"
                                        compField="addressComp"
                                        zipField="zipcode"
                                        cityField="city"
                                        countryField="country"
                                        address={selectedChild?.address?.address || ""}
                                        comp={selectedChild?.address?.addressComp || ""}
                                        zipcode={selectedChild?.address?.zipcode || ""}
                                        city={selectedChild?.address?.city || ""}
                                        country={selectedChild?.address?.country || "FR"}
                                        onChange={onChangeChildAddress}
                                        required={true}
                                        type="actual"
                                    />
                                    <div className="form-input">
                                        <label htmlFor="phone">Téléphone fixe</label>
                                        <input type="text" name="phone" value={selectedChild?.phone || ""}
                                               onChange={onChangeSelectedChild}/>
                                    </div>
                                    <div className="form-input">
                                        <label htmlFor="mobile">Téléphone mobile</label>
                                        <input type="text" name="mobile" value={selectedChild?.mobile || ""}
                                               onChange={onChangeSelectedChild}/>
                                    </div>
                                    <div className="form-input">
                                        <label htmlFor="email">Email</label>
                                        <input type="email" name="email" value={selectedChild?.email || ""}
                                               onChange={onChangeSelectedChild}/>
                                    </div>
                            </>
                        }
                    </>
                }
                {/* Le gestionnaire et le conjoint/enfant n'ont pas été choisis comme correspondant
                    (ou la question correspondante ne s'affiche même pas, ex: pas d'enfant majeur) :
                    afficher le formulaire vierge du correspondant dans tous les cas restants. */}
                {(values?.isCorrespondentAdmin == 0 &&
                  values?.isCorrespondentChild != 1 &&
                  (values?.isCorrespondentPartner != 1 || isConjointAdmin == 1)) &&
                    <>
                        <div className="form-group civility-title-group">
                            <CivilityForm
                                required={true}
                                onChange={onChangeNewCorrespondent}
                                value={newCorrespondent?.civility || ""}
                            />
                            {/*<div className="form-input">
                                <label htmlFor="title">Titre de civilité</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={newCorrespondent?.title || ""}
                                    onChange={onChangeNewCorrespondent}
                                    placeholder="Titre de civilité"
                                />
                            </div>*/}
                        </div>
                        <LastNameForm required={true} onChange={onChangeNewCorrespondent} value={newCorrespondent?.lastName || ""}/>
                        <div className="form-input">
                            <label htmlFor="birthName">Nom de naissance</label>
                            <input type="text" name="birthName" value={newCorrespondent?.birthName || ""}
                                   onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChangeNewCorrespondent(e); }}/>
                        </div>
                        <FirstnameForm required={true} onChange={onChangeNewCorrespondent} value={newCorrespondent?.firstName || ""}/>
                        <InputFullAddress
                            addressField="address"
                            compField="addressComp"
                            zipField="zipcode"
                            cityField="city"
                            countryField="country"
                            address={newCorrespondent?.address?.address || ""}
                            comp={newCorrespondent?.address?.addressComp || ""}
                            zipcode={newCorrespondent?.address?.zipcode || ""}
                            city={newCorrespondent?.address?.city || ""}
                            country={newCorrespondent?.address?.country || "FR"}
                            onChange={onChangeNewCorrespondentAddress}
                            required={true}
                            type="actual"
                        />
                        <div className="form-input">
                            <label htmlFor="phone">Téléphone fixe</label>
                            <input type="text" name="phone" value={newCorrespondent?.phone || ""}
                                   onChange={onChangeNewCorrespondent}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="mobile">Téléphone mobile</label>
                            <input type="text" name="mobile" value={newCorrespondent?.mobile || ""}
                                   onChange={onChangeNewCorrespondent}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="email">Email</label>
                            <input type="email" name="email" value={newCorrespondent?.email || ""}
                                   onChange={onChangeNewCorrespondent}/>
                        </div>
                        <div className="form-input">
                            <label htmlFor="familyAffiliation" className="required">Lien de parenté</label>
                            <select ref={affiliationRef} name="familyAffiliation" onChange={onChangeNewCorrespondent} value={newCorrespondent?.familyAffiliation || ""} required>
                                <option value="" disabled>Sélectionner un lien de parenté</option>
                                {newCorrespondent?.civility === "M" ?
                                    (affiliationList['maleAffiliation']).map((affiliation, index) => (
                                        <option key={index} defaultValue={correspondentInfos?.familyAffiliation == index + 1}
                                            name="familyAffiliation" value={index + 1}>{affiliation}</option>))
                                    : (affiliationList['femaleAffiliation']).map((affiliation, index) => (
                                        <option key={index} defaultValue={correspondentInfos?.familyAffiliation == index + 1}
                                            name="familyAffiliation" value={index + 1}>{affiliation}</option>))
                                }
                            </select>
                        </div>
                    </>
                }
                </> }
                <div className="errors"></div>
            </fieldset>
            {!noSubmitButton && (
                <div className="buttons-container">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="green-button"
                    >
                        <i className="fa fa-check"></i>
                        <span>Enregistrer les modifications</span>
                    </button>
                </div>
            )}
        </FormWrapper>
    )
}
